/**
 * Branch Context (Phase A — 2026-05-15)
 *
 * QR-order webapp version. In Phase A, QR scans + orders are all tagged
 * branch_id = 'huerth' (the only live branch). In Phase B/C, the QR-link
 * embeds &branch=koeln and BrandContext sets currentBranchId from the URL.
 */

export const DEFAULT_BRANCH_ID = 'huerth';

export const KNOWN_BRANCH_IDS = ['huerth', 'koeln', 'caesarstrasse', 'sanktaugustin', 'donvito'] as const;
export type BranchId = typeof KNOWN_BRANCH_IDS[number];

// ---- Branch registry (4-branch ready) ------------------------------------
// 2 branches active (Hürth, Köln); Branch 3 (Cäsarstraße) + Branch 4 reserved
// placeholders awaiting Mr. Sadik's details. Activate by filling in + setting
// status 'active'. Consumers iterate ACTIVE_BRANCH_IDS so new branches are
// picked up automatically.
export interface BranchSlot {
  id: string;
  name: string;
  city: string;
  status: 'active' | 'placeholder';
}
export const BRANCH_SLOTS: BranchSlot[] = [
  { id: 'huerth', name: 'SushiKi Hürth', city: 'Hürth', status: 'active' },
  { id: 'koeln', name: 'SushiKi Ossendorf', city: 'Köln', status: 'active' },
  { id: 'caesarstrasse', name: 'SushiKi Cäsarstraße', city: 'Köln', status: 'active' },
  { id: 'sanktaugustin', name: 'SushiKi Sankt Augustin', city: 'Sankt Augustin', status: 'active' },
  { id: 'donvito', name: 'Pizza Don Vito', city: 'Hürth', status: 'active' },
];
export const ACTIVE_BRANCH_IDS = BRANCH_SLOTS.filter((b) => b.status === 'active').map((b) => b.id);

const STORAGE_KEY = 'sushikiug:branch';

// Resolve branch at module load:
//   1. ?branch=koeln in URL  (one-shot scan link or marketing URL)
//   2. sessionStorage (sticky across pages within a session)
//   3. DEFAULT_BRANCH_ID ('huerth')
let _currentBranchId: string = DEFAULT_BRANCH_ID;
try {
  if (typeof window !== 'undefined') {
    const params = new URLSearchParams(window.location.search);
    const fromUrl = params.get('branch');
    if (fromUrl && (KNOWN_BRANCH_IDS as readonly string[]).includes(fromUrl)) {
      _currentBranchId = fromUrl;
      window.sessionStorage?.setItem(STORAGE_KEY, fromUrl);
    } else {
      const stored = window.sessionStorage?.getItem(STORAGE_KEY);
      if (stored && (KNOWN_BRANCH_IDS as readonly string[]).includes(stored)) {
        _currentBranchId = stored;
      }
    }
  }
} catch { /* SSR / locked-down browsers */ }

export const currentBranchId = (): string => _currentBranchId;

/** Categories hidden per branch (client-side filter — one shared Firestore menu). */
export const BRANCH_HIDDEN_CATEGORIES: Partial<Record<BranchId, string[]>> = {
  koeln: ['insalata', 'pasta', 'pizza', 'pizza don vito vorspeisen'],
  caesarstrasse: ['insalata', 'pasta', 'pizza', 'pizza don vito vorspeisen'],
};

/** Public contact info per SushiKi branch (landing page). */
export interface BranchContact { label: string; street: string; postalCity: string; phone: string; tel: string; }
export const BRANCH_CONTACT: Record<string, BranchContact> = {
  huerth: { label: 'Hürth', street: 'Sudetenstr. 60', postalCity: '50354 Hürth', phone: '02233 7063324', tel: '+4922337063324' },
  koeln: { label: 'Köln', street: 'Äußere Kanalstraße 209-211', postalCity: '50827 Köln', phone: '0221 56086538', tel: '+4922156086538' },
  caesarstrasse: { label: 'Cäsarstraße', street: 'Cäsarstraße 62', postalCity: '50968 Köln', phone: '0221 94650000', tel: '+4922194650000' },
  sanktaugustin: { label: 'Sankt Augustin', street: 'Mendener Straße 58', postalCity: '53757 Sankt Augustin', phone: '02241 3011427', tel: '+4922413011427' },
};
export const branchContact = (id?: string): BranchContact => BRANCH_CONTACT[id || _currentBranchId] || BRANCH_CONTACT.huerth;

export const getHiddenCategories = (branchId?: string): string[] =>
  BRANCH_HIDDEN_CATEGORIES[(branchId || _currentBranchId) as BranchId] || [];

export const setCurrentBranchId = (id: string): void => {
  _currentBranchId = id;
  try {
    if (typeof window !== 'undefined') {
      window.sessionStorage?.setItem(STORAGE_KEY, id);
    }
  } catch {}
};
